import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  type FollowUpQuestion,
  type MultiModalFormItem,
  type UrlStatus,
  type UserMsgCareTips,
  type ReplyStyle,
  FORM_ITEM_TYPE,
  CHAT_MSG_TYPE,
} from '@whelk/services/src/type/chat';
import {
  type SystemListItem,
  type ChatListUserItem,
  type ChatListSystemItem,
  type SystemCardUITypeEnum,
  DISABLE_EDIT_MSG_ID,
} from '@whelk/const';
import { manageMsgIds } from '@whelk/utils/src/chat';
import { RewriteItemType } from '../type';
import { removeVisibleMsgId } from '@whelk/utils/src/visibleMsgIds';
import i18n, { getTextByLang } from '@whelk/utils/src/i18n';

export const SEARCH_MODE_TYPE = {
  SEARCH: '0',
  NOT_SEARCH: '1',
};
export const SHARE_SELECTED_MSG_COUNT = 10;

export type SearchModeEnum = (typeof SEARCH_MODE_TYPE)[keyof typeof SEARCH_MODE_TYPE];

export enum ModelType {
  THINK = 'mm-m1',
  NOT_THINK = 'mm-01',
}

// 对话列表
export interface ChatStore {
  chatId: string;
  chatTitle: string;
  chatList: Array<ChatListUserItem | ChatListSystemItem>;
  searchMode: SearchModeEnum;
  isThinkMode: boolean;
  thinkMode: ModelType;
  canSearch: boolean;
  checkedMode: string;
  checkedMsgIds: string[];
  isCheckedLastCount: boolean;
  isMsgFeedbackModalOpen: boolean;
  isMsgDeleteModalOpen: boolean;
  operateTargetMsgId: string;
  msgIsDislike: Record<string, boolean>;
  followUpJson: Record<string, any>;
  followUpLoading: boolean;

  userInputBeforeLogin:
    | {
        textValue: string;
      }
    | undefined;
  savedUserInput: string;

  isShareSelecting: boolean;
  isShareSelectedAll: boolean;
  shareSelectedMsgIds: string[];
  rewriteConfig: RewriteItemType[];
  replyStyle?: ReplyStyle;
  editUserMsg?: ChatListUserItem;
  isEditUserCard: boolean;
  lastGroupChat: Array<ChatListUserItem | ChatListSystemItem>;
}

interface SetsystemListParams {
  index: number;
  newSystemList: SystemListItem[];
}

interface SetTargetSystemListIndexParams {
  chatIndex: number; // 对话列表index
  systemIndex: number; // 重试列表index
}

interface SetTargetSystemItemCardTypeParams {
  chatIndex: number; // 对话列表index
  systemIndex: number; // 重试列表index
  cardType: SystemCardUITypeEnum;
}
const initialState: ChatStore = {
  chatId: '0',
  chatTitle: getTextByLang('title_new_topic', i18n.language),
  chatList: [],
  canSearch: true,
  checkedMode: 'normal', // 处于分享状态
  checkedMsgIds: [],
  isCheckedLastCount: false,
  isMsgFeedbackModalOpen: false,
  isMsgDeleteModalOpen: false,
  operateTargetMsgId: '',
  msgIsDislike: {},
  followUpJson: {},
  followUpLoading: false,
  searchMode: SEARCH_MODE_TYPE.SEARCH,
  isThinkMode: true,
  thinkMode: ModelType.THINK,
  userInputBeforeLogin: undefined,
  savedUserInput: '',
  isShareSelecting: false,
  isShareSelectedAll: false,
  shareSelectedMsgIds: [],
  rewriteConfig: [],
  replyStyle: {},
  editUserMsg: {} as ChatListUserItem,
  isEditUserCard: false,
  lastGroupChat: [],
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearHistory: (state) => {
      return {
        ...state,
        chatId: '0',
        chatList: [],
      };
    },
    setChatList: (state, action: PayloadAction<Array<ChatListUserItem | ChatListSystemItem>>) => {
      return {
        ...state,
        chatList: [...action.payload],
      };
    },
    pushItems: (state, action: PayloadAction<Array<ChatListUserItem | ChatListSystemItem>>) => {
      return {
        ...state,
        chatList: [...state.chatList, ...action.payload],
      };
    },
    setChatId: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        chatId: action.payload,
      };
    },
    setChatTitle: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        chatTitle: action.payload,
      };
    },
    setSearchMode: (state, action: PayloadAction<SearchModeEnum>) => {
      return {
        ...state,
        searchMode: action.payload,
      };
    },
    setCanSearch: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        canSearch: action.payload,
      };
    },
    // 最后一条用户消息是否处于编辑状态
    setIsEditUserCard: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isEditUserCard: action.payload,
      };
    },
    // 消息回撤 删除最后两个消息 一个系统对话 一个用户对话
    deleteLastGroupChat: (state) => {
      const newList = [...state.chatList];
      newList.splice(newList.length - 2, 2);
      return {
        ...state,
        chatList: newList,
      };
    },
    setLastGroupChat: (
      state,
      action: PayloadAction<Array<ChatListUserItem | ChatListSystemItem>>,
    ) => {
      return {
        ...state,
        lastGroupChat: action.payload,
      };
    },
    // 消息先发出 需要同时修改 最后两个消息 一个系统对话 一个用户对话 的id, 以及用户消息的 careTips
    setInputSendMsgId: (
      state,
      action: PayloadAction<{
        userMsgId: string;
        systemMsgId: string;
        careTips?: UserMsgCareTips;
        userMsgContent?: string | undefined;
        form?: MultiModalFormItem[];
        longCutCopyWriting?: string;
        warningText?: string;
      }>,
    ) => {
      const newList = JSON.parse(JSON.stringify(state.chatList));
      const oldUserMsgContent = (newList[newList.length - 2] as ChatListUserItem)?.msgContent;
      if (newList[newList.length - 2]?.msgID?.includes(DISABLE_EDIT_MSG_ID)) {
        removeVisibleMsgId(newList[newList.length - 2].msgId);
        removeVisibleMsgId(newList[newList.length - 1].msgId);
      }
      newList[newList.length - 2] = {
        ...newList[newList.length - 2],
        msgId: action.payload.userMsgId,
        msgContent: action.payload?.userMsgContent || oldUserMsgContent,
        careTips: action.payload.careTips,
        multiModalForm: action.payload?.form?.length
          ? action.payload.form
          : newList[newList.length - 2]?.multiModalForm,
        longCutCopyWriting: action.payload.longCutCopyWriting,
        warningText: action.payload.warningText,
      } as ChatListUserItem;

      const lastSystemItem = { ...newList[newList.length - 1] };
      const systemList = lastSystemItem?.systemList || [];
      systemList[0].systemMsgID = action.payload.systemMsgId;

      newList[newList.length - 1] = {
        ...newList[newList.length - 1],
        msgId: action.payload.systemMsgId,
        parentMsgID: action.payload.userMsgId,
        systemList: systemList || [],
      } as ChatListSystemItem;

      return {
        ...state,
        chatList: newList,
      };
    },
    // 修改最后一个 msgId
    setLastItemSystemMsgID: (state, action: PayloadAction<string>) => {
      const newList = [...state.chatList];
      newList[newList.length - 1] = {
        ...newList[newList.length - 1],
        msgId: action.payload,
      };
      return {
        ...state,
        chatList: newList,
      };
    },
    // 修改最后一个 msgId
    setEditUserMsg: (state, action: PayloadAction<ChatListUserItem>) => {
      return {
        ...state,
        editUserMsg: action.payload,
      };
    },
    clearEditUserMsg: (state) => {
      return {
        ...state,
        editUserMsg: {} as ChatListUserItem,
      };
    },
    // 关闭最后一个信号收集
    setCloseLastSystemFeedback: (state, action: PayloadAction<{ index?: number }>) => {
      const newList = [...state.chatList];
      const targetChatItem = newList[newList.length - 1] as ChatListSystemItem;
      const lastSystemIndex = action.payload?.index || targetChatItem.systemList.length - 1;
      const targetChatItemSystemList = [...targetChatItem.systemList];

      targetChatItemSystemList[lastSystemIndex] = {
        ...targetChatItemSystemList[lastSystemIndex],
        feedbackPopUp: undefined,
      };
      newList[newList.length - 1] = {
        ...newList[newList.length - 1],
        systemList: targetChatItemSystemList,
      } as ChatListSystemItem;
      return {
        ...state,
        chatList: newList,
      };
    },
    // 修改对应 index 系统 返回列表
    setSystemList: (state, action: PayloadAction<SetsystemListParams>) => {
      const newList = JSON.parse(JSON.stringify(state.chatList)) as Array<
        ChatListUserItem | ChatListSystemItem
      >;
      newList[action.payload.index] = {
        ...newList[newList.length - 1],
        systemList: [...action.payload.newSystemList],
      } as ChatListSystemItem;
      return {
        ...state,
        chatList: newList,
      };
    },
    // 修改最后一条用户消息
    setUserCardMessage: (state, action: PayloadAction<{ msgId?: string; msgContent: string }>) => {
      const newList = JSON.parse(JSON.stringify(state.chatList)) as Array<
        ChatListUserItem | ChatListSystemItem
      >;
      // 分两种情况一种多模态一种普通文本 普通文本直接修改msgContent msgSubtype === CHAT_MSG_TYPE.MULTI_MODAL 的文本字段
      if (
        (newList[newList.length - 2] as ChatListUserItem)?.msgSubtype === CHAT_MSG_TYPE.MULTI_MODAL
      ) {
        const multiModalForm = (newList[newList.length - 2] as ChatListUserItem)?.multiModalForm;
        newList[newList.length - 2] = {
          ...newList[newList.length - 2],
          msgId: action.payload.msgId,
          multiModalForm: multiModalForm.map((item: MultiModalFormItem) => {
            if (item?.formType === FORM_ITEM_TYPE.TEXT) {
              return {
                ...item,
                content: action.payload.msgContent,
              };
            }
            return item;
          }),
        } as ChatListUserItem;
      } else {
        newList[newList.length - 2] = {
          ...newList[newList.length - 2],
          msgContent: action.payload.msgContent,
          msgId: action.payload.msgId,
        } as ChatListUserItem;
      }
      return {
        ...state,
        chatList: newList,
      };
    },
    // 修改对应 对话 系统system列表当前 index
    setTargetSystemListIndex: (state, action: PayloadAction<SetTargetSystemListIndexParams>) => {
      const newList = [...state.chatList];
      newList[action.payload.chatIndex] = {
        ...newList[newList.length - 1],
        targetSystemListIndex: action.payload.systemIndex,
      } as ChatListSystemItem;
      return {
        ...state,
        chatList: newList,
      };
    },
    // 修改 指定chat 的 对应的 cardType
    setTargetSystemItemCardType: (
      state,
      action: PayloadAction<SetTargetSystemItemCardTypeParams>,
    ) => {
      const newList = JSON.parse(JSON.stringify(state?.chatList)) as Array<
        ChatListUserItem | ChatListSystemItem
      >;
      const targetChatItem = newList?.[action.payload.chatIndex] as ChatListSystemItem;
      const targetChatItemSystemList = JSON.parse(JSON.stringify(targetChatItem?.systemList ?? []));
      targetChatItemSystemList[action.payload.systemIndex] = {
        ...targetChatItemSystemList[action.payload.systemIndex],
        cardType: action.payload.cardType,
      };
      newList[action.payload.chatIndex] = {
        ...newList[action.payload.chatIndex],
        systemList: targetChatItemSystemList,
      } as ChatListSystemItem;
      return {
        ...state,
        chatList: newList,
      };
    },
    // 修改最后一个 finishStatus
    setLastItemSystemFinishStatus: (state, action: PayloadAction<SystemCardUITypeEnum | null>) => {
      const newList = [...state.chatList];
      newList[newList.length - 1] = {
        ...newList[newList.length - 1],
        finishStatus: action.payload,
      } as ChatListSystemItem;
      return {
        ...state,
        chatList: newList,
      };
    },
    setIsMsgFeedbackModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isMsgFeedbackModalOpen = action.payload;
    },
    setIsMsgDeleteModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isMsgDeleteModalOpen = action.payload;
    },
    setOperateTargetMsgId: (state, action: PayloadAction<string>) => {
      state.operateTargetMsgId = action.payload;
    },
    setMsgIsDislike: (state, action: PayloadAction<{ msgId: string; isDislike: boolean }>) => {
      const msgIsDislike = {
        ...state.msgIsDislike,
        [action.payload.msgId]: action.payload.isDislike,
      };
      return {
        ...state,
        msgIsDislike,
      };
    },
    setFollowUp: (
      state,
      action: PayloadAction<{ msgID: string; followUpQuestions: FollowUpQuestion[] }>,
    ) => {
      const newFollowUpJson = state.followUpJson;
      newFollowUpJson[action.payload.msgID] = action.payload.followUpQuestions;
      state.followUpJson = newFollowUpJson;
    },
    setFollowUpLoading: (state, action: PayloadAction<boolean>) => {
      state.followUpLoading = action.payload;
    },
    setLastUserItemUrlStatus: (state, action: PayloadAction<UrlStatus>) => {
      const lastUserItem = state.chatList[state.chatList.length - 2];
      if (!lastUserItem || lastUserItem.msgType !== 'user') {
        return;
      }
      lastUserItem.urlStatus = action.payload;
    },
    setUserInputBeforeLogin: (
      state,
      action: PayloadAction<
        | {
            textValue: string;
          }
        | undefined
      >,
    ) => {
      state.userInputBeforeLogin = action.payload;
    },
    setSavedUserInput: (state, action: PayloadAction<string>) => {
      state.savedUserInput = action.payload;
    },
    setIsShareSelecting: (state, action: PayloadAction<boolean>) => {
      state.isShareSelecting = action.payload;
    },
    setIsShareSelectedAll: (state, action: PayloadAction<boolean>) => {
      state.isShareSelectedAll = action.payload;
    },
    clearShareSelectedMsgIds: (state) => {
      state.shareSelectedMsgIds = [];
    },
    setDestroyShare: (state) => {
      state.isShareSelecting = false;
      state.isShareSelectedAll = false;
      state.shareSelectedMsgIds = [];
    },
    toggleMsgShareSelect: (state, action: PayloadAction<string>) => {
      const checkedMsgIds = [...state.shareSelectedMsgIds];
      const chatList = [...state.chatList];
      state.shareSelectedMsgIds = manageMsgIds(action.payload, checkedMsgIds, chatList);
      state.isShareSelectedAll = state.shareSelectedMsgIds.length === SHARE_SELECTED_MSG_COUNT;
    },
    toggleMsgShareSelectAll: (state) => {
      if (state.isShareSelectedAll) {
        state.shareSelectedMsgIds = [];
        state.isShareSelectedAll = false;
      } else {
        const chatList = [...state.chatList];
        // 从某个位置向上去取，不包含这个位置 所以不是chatList.length-1
        let sliceEndNum = chatList.length;
        let count = SHARE_SELECTED_MSG_COUNT;
        // 对齐端上选择逻辑，多轮最旧的向上取20个，只有一轮向上取20个，没有选择从最新的取20个
        if (state.shareSelectedMsgIds.length) {
          count = count - state.shareSelectedMsgIds.length;
          // 获取选中的最旧的消息的index
          chatList.forEach((item, index) => {
            if (state.shareSelectedMsgIds.includes(item.msgId)) {
              sliceEndNum = Math.min(sliceEndNum, index);
            }
          });
        }
        const lastElementsMsgId = chatList
          .slice(Math.max(0, sliceEndNum - count), sliceEndNum)
          .map((item) => item.msgId)
          .concat(state.shareSelectedMsgIds);
        state.shareSelectedMsgIds = lastElementsMsgId;
        state.isShareSelectedAll = true;
      }
    },
    setRewriteConfig: (state, action: PayloadAction<RewriteItemType[]>) => {
      state.rewriteConfig = action.payload;
    },
    setReplyStyle: (state, action: PayloadAction<ReplyStyle>) => {
      state.replyStyle = action.payload;
    },
    setIsThinkMode: (state, action: PayloadAction<boolean>) => {
      state.isThinkMode = action.payload;
      if (action.payload) {
        state.thinkMode = ModelType.THINK;
      } else {
        state.thinkMode = ModelType.NOT_THINK;
      }
    },
  },
});

export const {
  pushItems,
  setChatId,
  setChatTitle,
  setChatList,
  setSearchMode,
  setCanSearch,
  clearHistory,
  setSystemList,
  setUserCardMessage,
  setCloseLastSystemFeedback,
  setInputSendMsgId,
  deleteLastGroupChat,
  setLastGroupChat,
  setLastItemSystemMsgID,
  setTargetSystemListIndex,
  setTargetSystemItemCardType,
  setLastItemSystemFinishStatus,
  setIsMsgFeedbackModalOpen,
  setIsMsgDeleteModalOpen,
  setOperateTargetMsgId,
  setMsgIsDislike,
  setFollowUp,
  setFollowUpLoading,
  setUserInputBeforeLogin,
  setLastUserItemUrlStatus,
  setSavedUserInput,
  setIsShareSelecting,
  setIsShareSelectedAll,
  setDestroyShare,
  clearShareSelectedMsgIds,
  toggleMsgShareSelect,
  toggleMsgShareSelectAll,
  setRewriteConfig,
  setReplyStyle,
  setEditUserMsg,
  clearEditUserMsg,
  setIsEditUserCard,
  setIsThinkMode,
} = chatSlice.actions;

export default chatSlice.reducer;
